import React, { useEffect, useState } from 'react'
import { FaCheck, FaEye, FaEyeSlash } from 'react-icons/fa6'

import {
    ChatMessageFieldsFragment,
    useUpdateMessageMutation,
} from '~graphql/generated/graphql'
import { MessageActionsProps } from './'
import { getRealPatients } from '~utils/helpers'
import MenuItem from '~components/shared/menu/menu-item'
import { useMessageProperties } from '../message-content/message-provider'

function getAssignedPatientIds(message: ChatMessageFieldsFragment): string[] {
    if (message.patients?.length) {
        return message.patients.map(p => p.id)
    }
    if (message.patient) {
        return [message.patient.id]
    }
    return []
}

// Forward ref
export default function AttachPatientSubmenu({
    message,
    chat,
}: MessageActionsProps) {
    const { refetchMessages } = useMessageProperties()
    const [showInactive, setShowInactive] = useState(false)
    const [assignedPatientIds, setAssignedPatientIds] = useState(() =>
        getAssignedPatientIds(message),
    )

    useEffect(() => {
        setAssignedPatientIds(getAssignedPatientIds(message))
    }, [message])

    const [updateMessage, { loading }] = useUpdateMessageMutation()

    const handleTogglePatient = async (patientId: string) => {
        const nextIds = assignedPatientIds.includes(patientId)
            ? assignedPatientIds.filter(id => id !== patientId)
            : [...assignedPatientIds, patientId]

        setAssignedPatientIds(nextIds)

        await updateMessage({
            variables: {
                chatId: chat.id,
                messageId: message.id,
                data: {
                    patientIds: nextIds,
                },
            },
            onCompleted: () => {
                refetchMessages()
            },
            onError: () => {
                setAssignedPatientIds(getAssignedPatientIds(message))
            },
        })
    }

    const sortedPatients = getRealPatients(chat.client.patients)
        .filter(
            patient =>
                !patient.is_deceased && (showInactive || patient.is_active),
        )
        .sort((a, b) => a.name?.localeCompare(b.name || '') || 0)

    return (
        <>
            <MenuItem
                icon={showInactive ? FaEye : FaEyeSlash}
                iconProps={{ color: showInactive ? 'brand.500' : 'gray.500' }}
                onClick={() => setShowInactive(prev => !prev)}
            >
                {showInactive ? 'Showing inactive' : 'Hiding inactive'}
            </MenuItem>
            {sortedPatients?.map(patient => {
                const isAssigned = assignedPatientIds.includes(patient.id)

                return (
                    <MenuItem
                        key={patient.id}
                        icon={isAssigned ? FaCheck : undefined}
                        onClick={() => handleTogglePatient(patient.id)}
                    >
                        {patient.name}
                        {!patient.is_active && ' (Inactive)'}
                    </MenuItem>
                )
            })}
        </>
    )
}
